import _ from './backdrop.module.scss'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'
import {useSharedStore} from '../state/shared-store-context'
import {$, $$$} from '../shared/class-name'

const BACKDROP_TOP_MARGIN = 300
const BACKDROP_BOTTOM_MARGIN = 300
const BACKDROP_CONTENT_Z_INDEX = 600

export const Backdrop = () => {
    const {sharedState, actions} = useSharedStore()
    const rootRef = useRef(null)
    const frameRef = useRef(null)
    const raisedElementRef = useRef(null)
    const raisedElementZIndexRef = useRef(null)
    const [bounds, setBounds] = useState({
        top: -BACKDROP_TOP_MARGIN,
        height: window.innerHeight + BACKDROP_TOP_MARGIN + BACKDROP_BOTTOM_MARGIN,
    })

    const updateBounds = useCallback(() => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current)
        frameRef.current = requestAnimationFrame(() => {
            const root = rootRef.current
            const containingBlock = root?.offsetParent || root?.parentElement
            if (!root || !containingBlock) return

            const viewport = window.visualViewport
            const viewportTop = viewport?.pageTop ?? window.scrollY
            const viewportHeight = Math.max(window.innerHeight, viewport?.height || 0)
            const containerTop = containingBlock.getBoundingClientRect().top + window.scrollY
            const nextBounds = {
                top: viewportTop - containerTop - BACKDROP_TOP_MARGIN,
                height: viewportHeight + BACKDROP_TOP_MARGIN + BACKDROP_BOTTOM_MARGIN,
            }

            setBounds(current =>
                current.top === nextBounds.top && current.height === nextBounds.height
                    ? current
                    : nextBounds
            )
            frameRef.current = null
        })
    }, [])
    const restoreRaisedElement = useCallback(() => {
        const element = raisedElementRef.current
        const zIndex = raisedElementZIndexRef.current
        if (element) {
            if (zIndex.value) element.style.setProperty('z-index', zIndex.value, zIndex.priority)
            else element.style.removeProperty('z-index')
        }
        raisedElementRef.current = null
        raisedElementZIndexRef.current = null
    }, [])

    useLayoutEffect(() => {
        const element = sharedState.backdropActive || sharedState.backdropInactivating
            ? sharedState.backdropActiveElement
            : null
        if (element === raisedElementRef.current) return
        restoreRaisedElement()
        if (!element) return
        raisedElementRef.current = element
        raisedElementZIndexRef.current = {
            value: element.style.getPropertyValue('z-index'),
            priority: element.style.getPropertyPriority('z-index'),
        }
        element.style.setProperty('z-index', BACKDROP_CONTENT_Z_INDEX)
    }, [sharedState.backdropActive, sharedState.backdropInactivating,
        sharedState.backdropActiveElement, restoreRaisedElement])
    useLayoutEffect(() => () => restoreRaisedElement(), [restoreRaisedElement])

    useLayoutEffect(() => {
        if (!sharedState.backdropActive && !sharedState.backdropInactivating) return undefined

        updateBounds()
        window.addEventListener('scroll', updateBounds, {passive: true})
        window.addEventListener('resize', updateBounds)
        window.visualViewport?.addEventListener('scroll', updateBounds, {passive: true})
        window.visualViewport?.addEventListener('resize', updateBounds)

        return () => {
            window.removeEventListener('scroll', updateBounds)
            window.removeEventListener('resize', updateBounds)
            window.visualViewport?.removeEventListener('scroll', updateBounds)
            window.visualViewport?.removeEventListener('resize', updateBounds)
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current)
                frameRef.current = null
            }
        }
    }, [sharedState.backdropActive, sharedState.backdropInactivating, updateBounds])

    useLayoutEffect(() => {
        if (!sharedState.backdropActive && !sharedState.backdropInactivating) return undefined

        const blockScrollInput = (e) => {
            if (e.cancelable) e.preventDefault()
        }
        const blockScrollKeys = (e) => {
            if (!['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) return
            if (e.cancelable) e.preventDefault()
        }

        window.addEventListener('wheel', blockScrollInput, {capture: true, passive: false})
        window.addEventListener('touchmove', blockScrollInput, {capture: true, passive: false})
        window.addEventListener('keydown', blockScrollKeys, {capture: true})

        return () => {
            window.removeEventListener('wheel', blockScrollInput, {capture: true})
            window.removeEventListener('touchmove', blockScrollInput, {capture: true})
            window.removeEventListener('keydown', blockScrollKeys, {capture: true})
        }
    }, [sharedState.backdropActive, sharedState.backdropInactivating])

    return <div className={$$$(
        $(_.root),
        $(_.active, sharedState.backdropActive),
        $(_.blocking, sharedState.backdropBlocking),
        $(_.inactivating, sharedState.backdropInactivating),
        $(_.inactive, !sharedState.backdropActive && !sharedState.backdropInactivating),
    )}
                ref={rootRef}
                style={{top: `${bounds.top}px`, height: `${bounds.height}px`}}
                onClick={() => actions.deactivateBackdrop()}/>
}
